import {
  type DeliveryBenchmarkLane,
  isTerminalArrivalStopType,
  type LaneAvailability,
  type MLTripSummary,
  type ResolvedDeliveryBenchmark,
  type StopBenchmarkFields,
  type StopBenchmarkFieldSource,
  type StopSummary,
} from "../types";

export type {
  DeliveryBenchmarkLane,
  DeliveryBenchmarkLockReason,
  LaneAvailability,
  ResolvedDeliveryBenchmark,
  StopBenchmarkDeviationKey,
  StopBenchmarkFields,
  StopBenchmarkFieldSource,
  StopBenchmarkStatusKey,
} from "../types";

export const TOWERS_DELIVERY_BENCHMARK_STORAGE_KEY = "towersDeliveryBenchmark";

export const DEFAULT_DELIVERY_BENCHMARK_LANE: DeliveryBenchmarkLane =
  "contractual";

export function stopBenchmarkFields(
  lane: DeliveryBenchmarkLane,
): StopBenchmarkFields {
  if (lane === "empirical") {
    return {
      statusKey: "empiricalOnTimeStatus",
      deviationKey: "empiricalDeviationMinutes",
    };
  }
  return {
    statusKey: "contractualOnTimeStatus",
    deviationKey: "contractualDeviationMinutes",
  };
}

/**
 * True when the backend provided a usable on-time label (not empty / UNKNOWN).
 */
export function hasKnownBenchmarkStatus(status: string | undefined): boolean {
  return (
    status != null &&
    status !== "" &&
    status !== "UNKNOWN" &&
    status !== "ON_TIME_STATUS_UNKNOWN"
  );
}

export function isStopEligibleForBenchmark(stop: StopSummary): boolean {
  return !isTerminalArrivalStopType(stop.stopType);
}

/** Benchmark-eligible delivery stop that has actually arrived (filled status dot). */
export function isArrivedBenchmarkEligibleStop(stop: StopSummary): boolean {
  return (
    isStopEligibleForBenchmark(stop) &&
    stop.arrivalTime != null &&
    stop.arrivalTime !== ""
  );
}

export function aggregateLaneAvailability(
  trips: MLTripSummary[],
): LaneAvailability {
  let hasContractual = false;
  let hasEmpirical = false;

  for (const trip of trips) {
    for (const stop of trip.stopSummaries) {
      if (!isStopEligibleForBenchmark(stop)) {
        continue;
      }
      if (hasKnownBenchmarkStatus(stop.contractualOnTimeStatus)) {
        hasContractual = true;
      }
      if (hasKnownBenchmarkStatus(stop.empiricalOnTimeStatus)) {
        hasEmpirical = true;
      }
      if (hasContractual && hasEmpirical) {
        return {
          hasContractual: true,
          hasEmpirical: true,
          hasAny: true,
        };
      }
    }
  }

  return {
    hasContractual,
    hasEmpirical,
    hasAny: hasContractual || hasEmpirical,
  };
}

/** Lane availability for deliveries at a single site (cluster) on completed trips. */
export function aggregateLaneAvailabilityForCluster(
  trips: MLTripSummary[],
  clusterId: string,
): LaneAvailability {
  let hasContractual = false;
  let hasEmpirical = false;

  for (const trip of trips) {
    if (trip.currentStage !== "COMPLETED") {
      continue;
    }
    for (const stop of trip.stopSummaries) {
      if (stop.clusterId !== clusterId || !isStopEligibleForBenchmark(stop)) {
        continue;
      }
      if (hasKnownBenchmarkStatus(stop.contractualOnTimeStatus)) {
        hasContractual = true;
      }
      if (hasKnownBenchmarkStatus(stop.empiricalOnTimeStatus)) {
        hasEmpirical = true;
      }
      if (hasContractual && hasEmpirical) {
        return {
          hasContractual: true,
          hasEmpirical: true,
          hasAny: true,
        };
      }
    }
  }

  return {
    hasContractual,
    hasEmpirical,
    hasAny: hasContractual || hasEmpirical,
  };
}

/** True when completed trips lack known benchmark labels for the active lane. */
export function showBenchmarkNaForActiveLane(
  availability: LaneAvailability,
  lane: DeliveryBenchmarkLane,
): boolean {
  if (lane === "empirical") {
    return !availability.hasEmpirical;
  }
  return !availability.hasContractual;
}

export function clusterHasKnownBenchmarkForLane(
  trips: MLTripSummary[],
  clusterId: string,
  lane: DeliveryBenchmarkLane,
): boolean {
  const availability = aggregateLaneAvailabilityForCluster(trips, clusterId);
  return !showBenchmarkNaForActiveLane(availability, lane);
}

export function resolveDeliveryBenchmark({
  preference,
  availability,
}: {
  preference: DeliveryBenchmarkLane;
  availability: LaneAvailability;
}): ResolvedDeliveryBenchmark {
  const { hasContractual, hasEmpirical } = availability;

  if (hasContractual && !hasEmpirical) {
    return {
      lane: "contractual",
      toggleChecked: true,
      isLocked: true,
      lockReason: "contractualOnly",
    };
  }

  if (!hasContractual && hasEmpirical) {
    return {
      lane: "empirical",
      toggleChecked: false,
      isLocked: true,
      lockReason: "empiricalOnly",
    };
  }

  if (!hasContractual && !hasEmpirical) {
    return {
      lane: preference,
      toggleChecked: preference === "contractual",
      isLocked: true,
      lockReason: "noBenchmarkData",
    };
  }

  return {
    lane: preference,
    toggleChecked: preference === "contractual",
    isLocked: false,
    lockReason: null,
  };
}

export function getStopBenchmarkStatus(
  stop: StopBenchmarkFieldSource,
  lane: DeliveryBenchmarkLane,
): string | undefined {
  const { statusKey } = stopBenchmarkFields(lane);
  return stop[statusKey];
}

export function getStopBenchmarkDeviationMinutes(
  stop: StopBenchmarkFieldSource,
  lane: DeliveryBenchmarkLane,
): number | undefined {
  const { deviationKey } = stopBenchmarkFields(lane);
  return stop[deviationKey];
}

/** True when at least one eligible stop on the trip has a known benchmark status for the lane. */
export function tripHasBenchmarkForLane(
  trip: MLTripSummary,
  lane: DeliveryBenchmarkLane,
): boolean {
  for (const stop of trip.stopSummaries) {
    if (!isStopEligibleForBenchmark(stop)) {
      continue;
    }
    if (hasKnownBenchmarkStatus(getStopBenchmarkStatus(stop, lane))) {
      return true;
    }
  }
  return false;
}
